import React, { useState } from 'react';
import api from '../api';
import {
  Robot, SpinnerGap, CheckCircle, FloppyDisk, ArrowRight, Warning, Target, Lightbulb
} from '@phosphor-icons/react';

export default function ObjectiveAuthor({ onSaved }) {
  const [topic, setTopic] = useState('');
  const [scope, setScope] = useState('');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const generateDraft = async () => {
    if (!topic.trim()) return;
    setLoading(true); setDraft(null); setError(''); setSaved(false);
    try {
      const resp = await api.post('/api/env/objectives/draft', { topic, scope });
      if (resp.data.draft?.error) { setError(resp.data.draft.error); }
      else { setDraft(resp.data.draft); }
    } catch { setError('Failed to generate draft'); }
    setLoading(false);
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true); setError('');
    try {
      await api.post('/api/env/objectives/save', { objective: draft });
      setSaved(true);
      if (onSaved) onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div data-testid="objective-author" className="space-y-4">
      <div className="bg-white border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb size={16} className="text-brand-accent" />
          <h3 className="font-heading font-semibold text-sm text-content-primary">AI Objective Authoring Assistant</h3>
        </div>
        <p className="text-[10px] text-content-secondary mb-3">Describe a sustainability topic and the AI will draft a complete objective with KPIs, strategies, and measures.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="md:col-span-2">
            <label className="block text-[9px] font-semibold text-content-secondary uppercase mb-1">Topic / Focus Area</label>
            <input data-testid="author-topic" type="text" value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g., Fleet electrification, Indoor air quality, Biodiversity protection..." className="w-full px-3 py-2 border border-border-default rounded-lg text-xs" />
          </div>
          <div>
            <label className="block text-[9px] font-semibold text-content-secondary uppercase mb-1">Scope</label>
            <input data-testid="author-scope" type="text" value={scope} onChange={e => setScope(e.target.value)} placeholder="e.g., All BSP facilities" className="w-full px-3 py-2 border border-border-default rounded-lg text-xs" />
          </div>
        </div>
        <button data-testid="author-generate" onClick={generateDraft} disabled={loading || !topic.trim()} className="mt-3 px-4 py-2 bg-brand-accent text-white rounded-lg text-xs font-semibold hover:bg-brand-accent/90 disabled:opacity-50 flex items-center gap-2">
          {loading ? <><SpinnerGap size={14} className="animate-spin" />Drafting...</> : <><Robot size={14} />Generate Objective Draft</>}
        </button>
      </div>

      {error && <div className="p-3 bg-status-alert-bg rounded-lg border border-status-alert/20 text-xs text-status-alert">{error}</div>}

      {draft && (
        <div data-testid="author-draft" className="bg-white border border-brand-primary/20 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] font-mono text-content-disabled">{draft.objective_id}</span>
                <span className="px-2 py-0.5 bg-brand-accent/10 text-brand-accent text-[8px] font-bold rounded-full">AI DRAFT</span>
              </div>
              <h3 className="font-heading font-semibold text-base text-content-primary">{draft.title}</h3>
            </div>
            {saved ? (
              <span className="flex items-center gap-1 text-status-on-track text-xs font-semibold"><CheckCircle size={14} />Saved</span>
            ) : (
              <button data-testid="author-save" onClick={saveDraft} disabled={saving} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-xs font-semibold hover:bg-brand-primary-hover disabled:opacity-50 flex items-center gap-1">
                {saving ? <SpinnerGap size={12} className="animate-spin" /> : <FloppyDisk size={12} />} Save to Registry
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
            <div><span className="text-content-disabled">Category:</span> <span className="font-semibold text-content-primary">{draft.category}</span></div>
            <div><span className="text-content-disabled">Baseline Year:</span> <span className="font-semibold">{draft.baseline_year}</span></div>
            <div><span className="text-content-disabled">Target Year:</span> <span className="font-semibold">{draft.target_year}</span></div>
            <div><span className="text-content-disabled">Owner:</span> <span className="font-semibold">{draft.owner}</span></div>
          </div>

          <div>
            <p className="text-[10px] text-content-secondary">{draft.rationale}</p>
          </div>

          {/* KPIs */}
          {draft.kpis?.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-content-secondary uppercase mb-2 flex items-center gap-1"><Target size={10} /> Suggested KPIs</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {draft.kpis.map((k, i) => (
                  <div key={i} className="p-2.5 bg-surface-default rounded-lg border border-border-subtle">
                    <p className="text-[11px] font-semibold text-content-primary">{k.name}</p>
                    <div className="flex gap-3 mt-1 text-[9px] text-content-disabled">
                      <span>Unit: {k.unit}</span>
                      <span>Baseline: {k.baseline}</span>
                      <span>Target: {k.target}</span>
                      {k.suggested_current !== undefined && <span>Current: {k.suggested_current}</span>}
                    </div>
                    <p className="text-[9px] text-content-disabled mt-0.5">Source: {k.data_source}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strategies */}
          {draft.strategies?.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-content-secondary uppercase mb-2 flex items-center gap-1"><ArrowRight size={10} /> Suggested Strategies</h4>
              <div className="space-y-1.5">
                {draft.strategies.map((s, i) => (
                  <div key={i} className="p-2.5 bg-surface-default rounded-lg border border-border-subtle text-[11px]">
                    <p className="font-semibold text-content-primary">{s.name}: {s.measure}</p>
                    <p className="text-content-disabled text-[9px]">{s.office} | {s.milestone} | Budget: {s.budget} | Outcome: {s.outcome}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Rationale */}
          {draft.ai_rationale && (
            <div className="p-3 bg-brand-accent/5 rounded-lg border border-brand-accent/10 flex items-start gap-2">
              <Robot size={14} className="text-brand-accent mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-content-secondary italic">{draft.ai_rationale}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
